<template>
  <div :class="[!autoHeight ? 'h-96' : '']">
    <!-- SCORECARD MODE -->
    <template v-if="viewMode === 'scorecard'">
      <div v-if="hasAllMetrics || (rowData.length && !loading)" class="flex items-center justify-end gap-4 mb-2">
        <label v-if="hasAllMetrics" class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            v-model="showAll"
            class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark h-3.5 w-3.5"
          />
          Show all metrics
        </label>
        <ExportMenu
          :disabled="!rowData.length"
          :exports="[
            { type: 'pptx', action: () => exportFile('pptx') },
            { type: 'pdf', action: () => exportFile('pdf') },
            { type: 'excel', action: () => exportFile('xlsx') },
          ]"
        />
      </div>
      <div v-if="loading" class="border border-gray-200 rounded-lg">
        <LoadingIndicator message="Loading metrics..." />
      </div>
      <div v-else-if="!rowData.length" class="flex items-center justify-center py-12 text-gray-500 text-sm border border-gray-200 rounded-lg">
        No metric data available
      </div>
      <div v-else class="border border-gray-200 rounded-lg overflow-hidden">
        <table ref="scorecard" class="w-full text-xs border-collapse table-fixed">
          <colgroup>
            <col style="width: 24%" />
            <template v-for="g in orderedScorecardGroups" :key="g.id">
              <col v-for="col in g.cols" :key="col.key" :style="`width: ${col.width}`" />
            </template>
          </colgroup>
          <thead>
            <tr class="border-b border-gray-200 bg-gray-50">
              <th class="text-left px-3 py-2 text-xs font-medium text-gray-600" rowspan="2">Metric Name</th>
              <th
                v-for="g in orderedScorecardGroups"
                :key="g.id"
                class="text-center px-2 py-2 text-xs font-medium text-gray-600 cursor-grab select-none"
                :class="[
                  dragGroupId === g.id ? 'opacity-50' : '',
                  g.hasSubLabels ? 'border-b border-gray-200' : '',
                ]"
                :colspan="g.hasSubLabels ? g.cols.length : null"
                :rowspan="g.hasSubLabels ? null : 2"
                draggable="true"
                @dragstart="onGroupDragStart(g.id)"
                @dragover="onGroupDragOver($event, g.id)"
                @dragend="onGroupDragEnd"
              >{{ g.label }}</th>
            </tr>
            <tr class="border-b border-gray-200 bg-gray-50">
              <template v-for="g in orderedScorecardGroups" :key="g.id">
                <template v-if="g.hasSubLabels">
                  <th
                    v-for="col in g.cols"
                    :key="col.key"
                    class="text-center px-2 py-1.5 text-xs font-medium text-gray-500"
                  >{{ col.subLabel }}</th>
                </template>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in orderedRowData"
              :key="row.metric_id"
              class="border-b border-gray-100 hover:bg-gray-50 cursor-grab select-none"
              :class="dragRowId === row.metric_id ? 'opacity-50' : ''"
              draggable="true"
              @dragstart="onRowDragStart(row.metric_id)"
              @dragover="onRowDragOver($event, row.metric_id)"
              @dragend="onRowDragEnd"
            >
              <td class="px-3 py-2.5">
                <router-link
                  :to="metricLink(row)"
                  class="text-blue-600 hover:underline font-medium"
                >{{ row.metric_name }}</router-link>
                <div v-if="row.metadata && row.metadata.denom_start_date" class="text-[10px] text-gray-400 mt-0.5">{{ formatDate(row.metadata.denom_start_date) }} – {{ formatDate(row.metadata.denom_end_date) }}</div>
              </td>
              <template v-for="g in orderedScorecardGroups" :key="g.id">
                <template v-for="col in g.cols" :key="col.key">
                  <td v-if="col.key === 'current'" class="text-center px-2 py-2.5 font-medium tabular-nums" :class="rateClass(row)">
                    {{ formatRate(row) }}
                  </td>
                  <td v-else-if="col.key === 'previous'" class="text-center px-2 py-2.5 tabular-nums text-gray-500">
                    {{ formatPrevRate(row) }}
                  </td>
                  <td v-else-if="col.key === 'trend'" class="text-center px-2 py-2.5 tabular-nums">
                    <span v-if="row.trend_slope != null" :class="trendClass(row)">
                      {{ formatTrend(row) }}
                    </span>
                    <span v-else class="text-gray-300">—</span>
                  </td>
                  <td v-else-if="col.key === 'ytd'" class="text-center px-2 py-2.5 text-gray-700 tabular-nums">
                    {{ row.metric_formatting === '%' && isYTD(row) ? formatNumber(row.numerator) : '—' }}
                  </td>
                  <td v-else-if="col.key === 'last_month'" class="text-center px-2 py-2.5 text-gray-500 tabular-nums">
                    {{ row.metric_formatting === '%' && isYTD(row) ? formatNumber(row.prev_numerator) : '—' }}
                  </td>
                  <td v-else-if="col.key === 'remaining_opp'" class="text-center px-2 py-2.5 text-gray-700 tabular-nums">
                    {{ row.metric_formatting === '%' ? formatNumber(remainingOpp(row)) : '—' }}
                  </td>
                  <td v-else-if="col.key === 'target'" class="text-center px-2 py-2.5 text-gray-700 tabular-nums">
                    {{ row.metric_formatting === '%' ? formatTarget(row) : '—' }}
                  </td>
                  <td v-else-if="col.key === 'pt_lives'" class="text-center px-2 py-2.5 text-gray-700 tabular-nums">
                    {{ formatNumber(row.attribution) }}
                  </td>
                  <td v-else-if="col.key === 'most_recent'" class="text-center px-2 py-2.5 text-gray-500">
                    {{ formatDate(row.most_recent_ov_claim_date || row.observation_date) }}
                  </td>
                </template>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="rowData.length && !loading" class="mt-2">
        <div class="text-[10px] italic text-gray-400">
          ¹ Statistical trend calculated using linear regression over the last 12 weeks (monthly rate of change).
        </div>
      </div>
    </template>

    <!-- PIVOT MODE -->
    <template v-else-if="viewMode === 'pivot'">
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <!-- Entity type selector -->
        <div class="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
          <div class="inline-flex items-center gap-2">
            <span class="text-xs font-semibold text-gray-600">Entity Type</span>
            <select
              v-model="pivotEntityType"
              class="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-dark"
            >
              <option v-for="et in pivotEntityTypes" :key="et" :value="et">{{ et }}</option>
            </select>
          </div>
          <ExportMenu
            :disabled="!pivotRows.length"
            :exports="[
              { type: 'pptx', action: () => exportPivotFile('pptx') },
              { type: 'pdf', action: () => exportPivotFile('pdf') },
              { type: 'excel', action: () => exportPivotFile('xlsx') },
            ]"
          />
        </div>

        <!-- Metric chips -->
        <div class="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
          <span class="text-xs font-semibold text-gray-600">Metrics</span>
          <span
            v-for="m in pivotVisibleMetrics"
            :key="m.id"
            class="inline-flex items-center gap-1 bg-secondary-100 border border-secondary-300 text-secondary-900 px-2.5 py-0.5 rounded text-xs font-medium"
          >
            {{ m.name }}
            <button
              v-if="pivotVisibleMetrics.length > 1"
              @click="removePivotMetric(m.id)"
              class="text-secondary-600 hover:text-secondary-900 ml-0.5 leading-none transition-colors"
            >&times;</button>
          </span>
          <select
            v-if="pivotHiddenMetrics.length"
            class="text-xs text-gray-500 bg-transparent border-0 py-0.5 pl-1 pr-4 cursor-pointer focus:outline-none"
            @change="addPivotMetric(Number($event.target.value)); $event.target.value = ''"
          >
            <option value="">+ Metric</option>
            <option v-for="m in pivotHiddenMetrics" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
        </div>

        <!-- Pivot table -->
        <LoadingIndicator v-if="loading" message="Loading metrics..." />
        <div v-else-if="!pivotRows.length" class="flex items-center justify-center py-12 text-gray-500 text-sm">
          No metric data available
        </div>
        <div v-else class="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  class="px-4 py-2.5 text-left text-xs font-medium text-gray-600 cursor-pointer select-none"
                  @click="doPivotSort('entity_name')"
                >
                  <span class="inline-flex items-center gap-1">
                    {{ pivotEntityType }}
                    <span v-if="pivotSortKey === 'entity_name'" class="text-gray-400 text-[10px]">{{ pivotSortAsc ? '▲' : '▼' }}</span>
                  </span>
                </th>
                <th
                  v-for="m in pivotActiveMetrics"
                  :key="m.id"
                  class="px-4 py-2.5 text-left text-xs font-medium text-gray-600 cursor-grab select-none"
                  :class="dragColumnId === m.id ? 'opacity-50' : ''"
                  style="min-width: 200px"
                  draggable="true"
                  @dragstart="onColumnDragStart(m.id)"
                  @dragover="onColumnDragOver($event, m.id)"
                  @dragend="onColumnDragEnd"
                  @click="doPivotSort('metric_' + m.id)"
                >
                  <span class="inline-flex items-center gap-1">
                    {{ m.name }}
                    <span v-if="pivotSortKey === 'metric_' + m.id" class="text-gray-400 text-[10px]">{{ pivotSortAsc ? '▲' : '▼' }}</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in pivotSorted"
                :key="row.entity_id"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td class="sensitive px-4 py-2.5 text-xs font-medium text-gray-800">{{ row.entity_name }}</td>
                <td
                  v-for="m in pivotActiveMetrics"
                  :key="m.id"
                  class="px-4 py-2"
                  @mouseenter="showPivotTooltip(row, m, $event)"
                  @mousemove="movePivotTooltip($event)"
                  @mouseleave="pivotTooltipRow = null"
                >
                  <div v-if="row['denom_' + m.id]" class="flex items-center gap-3">
                    <div class="relative flex-1 h-3.5 bg-gray-100 rounded-sm overflow-hidden" style="min-width: 120px">
                      <div
                        class="absolute inset-y-0 left-0 rounded-sm transition-all"
                        :class="pivotBarColor(row['metric_' + m.id], m)"
                        :style="{ width: Math.min((row['metric_' + m.id] || 0) * 100, 100) + '%' }"
                      ></div>
                    </div>
                    <span
                      class="text-xs font-semibold w-12 text-right tabular-nums"
                      :class="pivotTextColor(row['metric_' + m.id], m)"
                    >{{ pivotFormatRate(row['metric_' + m.id], m) }}</span>
                  </div>
                  <div v-else class="text-gray-400 text-xs">-</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pivot tooltip -->
      <Teleport to="body">
        <div
          v-if="pivotTooltipRow"
          class="fixed z-50 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-lg px-3 py-2 pointer-events-none"
          :style="{ top: pivotTooltipPos.y + 'px', left: pivotTooltipPos.x + 'px' }"
        >
          <div class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Rate</span>
            <span class="font-semibold tabular-nums">{{ pivotTooltipRateFormatted }}</span>
          </div>
          <div class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Numerator</span>
            <span class="tabular-nums">{{ pivotTooltipRow.num?.toLocaleString() }}</span>
          </div>
          <div class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Denominator</span>
            <span class="tabular-nums">{{ pivotTooltipRow.denom?.toLocaleString() }}</span>
          </div>
          <div v-if="pivotTooltipRow.attribution" class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Patients</span>
            <span class="tabular-nums">{{ pivotTooltipRow.attribution?.toLocaleString() }}</span>
          </div>
        </div>
      </Teleport>



    </template>
  </div>
</template>

<script>
import axios from 'axios';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'ComposableMetricScorecard',
  components: { ExportMenu, LoadingIndicator },
  props: {
    entity: { type: Object, default: () => ({}) },
    entityTypeName: { type: String, default: 'Practice' },
    observationYear: { type: Number, default: null },
    metricIds: { type: Array, default: () => [] },
    allMetricIds: { type: Array, default: () => [] },
    metricNames: { type: Object, default: () => ({}) },
    autoHeight: { type: Boolean, default: false },
    viewMode: { type: String, default: 'scorecard' }, // 'scorecard' | 'pivot'
  },
  data() {
    return {
      // Scorecard
      rowData: [],
      loading: true,
      showAll: false,
      sparkWidth: 120,
      sparkHeight: 24,
      sparkPadding: 2,
      // Pivot
      pivotEntityType: 'Practice',
      pivotEntityTypes: ['Practice', 'Staff', 'Location'],
      pivotRawData: [],
      pivotAddedMetricIds: [],
      pivotRemovedMetricIds: [],
      pivotMetricMeta: {},  // { id: { name, display_config } }
      pivotSortKey: 'entity_name',
      pivotSortAsc: true,
      pivotTooltipRow: null,
      pivotTooltipPos: { x: 0, y: 0 },
      pivotTooltipMetric: null,
      pivotMetricOrder: null,
      dragColumnId: null,
      scorecardMetricOrder: null,
      dragRowId: null,
      scorecardColumnOrder: null,
      dragGroupId: null,
    };
  },
  computed: {
    activeMetricIds() {
      return this.showAll && this.allMetricIds.length ? this.allMetricIds : this.metricIds;
    },
    hasAllMetrics() {
      return this.allMetricIds.length > this.metricIds.length;
    },
    orderedRowData() {
      if (!this.scorecardMetricOrder) return this.rowData;
      const orderMap = new Map(this.scorecardMetricOrder.map((id, i) => [id, i]));
      return this.rowData.slice().sort((a, b) => {
        const ai = orderMap.has(a.metric_id) ? orderMap.get(a.metric_id) : Infinity;
        const bi = orderMap.has(b.metric_id) ? orderMap.get(b.metric_id) : Infinity;
        return ai - bi;
      });
    },
    scorecardGroupDefs() {
      const defs = [
        { id: 'performance', label: 'Performance', cols: [
          { key: 'current', subLabel: 'Current', width: '8%' },
          { key: 'previous', subLabel: 'Previous', width: '8%' },
        ]},
        { id: 'trend', label: 'Trend¹', cols: [{ key: 'trend', subLabel: 'Monthly', width: '8%' }] },
        { id: 'completed', label: 'Completed', cols: [
          { key: 'ytd', subLabel: 'YTD', width: '8%' },
          { key: 'last_month', subLabel: 'Last Month', width: '8%' },
        ]},
        { id: 'remaining_opp', label: 'Remaining Opp', cols: [{ key: 'remaining_opp', width: '8%' }] },
        { id: 'target', label: 'Target (month)', cols: [{ key: 'target', width: '7%' }] },
        { id: 'pt_lives', label: 'Pt Lives', cols: [{ key: 'pt_lives', width: '7%' }] },
        { id: 'most_recent', label: 'Most Recent Claims Received', cols: [{ key: 'most_recent', width: '14%' }] },
      ];
      return defs.map(g => ({ ...g, hasSubLabels: g.cols.some(c => !!c.subLabel) }));
    },
    orderedScorecardGroups() {
      if (!this.scorecardColumnOrder) return this.scorecardGroupDefs;
      const orderMap = new Map(this.scorecardColumnOrder.map((id, i) => [id, i]));
      return this.scorecardGroupDefs.slice().sort((a, b) => {
        const ai = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
        const bi = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
        return ai - bi;
      });
    },
    // Pivot computeds
    pivotAllMetrics() {
      // All metrics available for pivot chips — names from prop, display_config from response
      return this.allMetricIds.map(id => ({
        id,
        name: this.pivotMetricMeta[id]?.name || this.metricNames[id] || `Metric ${id}`,
        display_config: this.pivotMetricMeta[id]?.display_config || {},
      }));
    },
    pivotVisibleMetricIds() {
      // Start with key metric IDs, user can add/remove via chips
      const visible = new Set(this.metricIds);
      // Add any manually added
      for (const id of this.pivotAddedMetricIds) visible.add(id);
      // Remove any manually removed
      for (const id of this.pivotRemovedMetricIds) visible.delete(id);
      return [...visible];
    },
    pivotVisibleMetrics() {
      const vis = new Set(this.pivotVisibleMetricIds);
      return this.pivotAllMetrics.filter(m => vis.has(m.id));
    },
    pivotActiveMetrics() {
      if (!this.pivotMetricOrder) return this.pivotVisibleMetrics;
      const orderMap = new Map(this.pivotMetricOrder.map((id, i) => [id, i]));
      return this.pivotVisibleMetrics.slice().sort((a, b) => {
        const ai = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
        const bi = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
        return ai - bi;
      });
    },
    pivotHiddenMetrics() {
      const vis = new Set(this.pivotVisibleMetricIds);
      return this.pivotAllMetrics.filter(m => !vis.has(m.id));
    },
    pivotActiveMetricIds() {
      return this.pivotVisibleMetricIds;
    },
    pivotRows() {
      // Reshape: one row per entity, metric rates as columns
      const map = {};
      for (const row of this.pivotRawData) {
        const key = row.entity_id;
        if (!map[key]) {
          map[key] = {
            entity_id: row.entity_id,
            entity_name: row.entity_name,
            entity_type: row.entity_type,
          };
        }
        map[key]['metric_' + row.metric_id] = row.rate;
        map[key]['num_' + row.metric_id] = row.numerator;
        map[key]['denom_' + row.metric_id] = row.denominator;
        map[key]['attr_' + row.metric_id] = row.attribution;
      }
      return Object.values(map);
    },
    pivotSorted() {
      const rows = [...this.pivotRows];
      const key = this.pivotSortKey;
      rows.sort((a, b) => {
        const av = a[key] ?? '';
        const bv = b[key] ?? '';
        if (typeof av === 'number' && typeof bv === 'number') {
          return this.pivotSortAsc ? av - bv : bv - av;
        }
        return this.pivotSortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
      return rows;
    },
    pivotTooltipRateFormatted() {
      if (!this.pivotTooltipRow || !this.pivotTooltipMetric) return '-';
      const rate = this.pivotTooltipRow.rate;
      if (rate == null) return '-';
      const dc = this.pivotTooltipMetric.display_config || {};
      if (dc.format === 'decimal' || dc.format === 'number') return rate.toFixed(dc.decimal_places ?? 1);
      return (rate * 100).toFixed(dc.decimal_places ?? 1) + '%';
    },
  },
  watch: {
    observationYear() { this.fetchActiveMode(); },
    'entity.id'() { this.fetchActiveMode(); },
    activeMetricIds: { handler() { if (this.viewMode === 'scorecard') this.fetchScorecardData(); }, deep: true },
    viewMode() { this.fetchActiveMode(); },
    pivotEntityType() { this.fetchPivotData(); },
    pivotActiveMetricIds: {
      handler() { if (this.viewMode === 'pivot') this.fetchPivotData(); },
      deep: true,
    },
  },
  mounted() {
    this.fetchActiveMode();
  },
  methods: {
    getExportActions() {
      if (this.loading) return [];
      if (this.viewMode === 'pivot') {
        if (!this.pivotRows.length) return [];
        return [
          { type: 'pptx', action: () => this.exportPivotFile('pptx') },
          { type: 'pdf', action: () => this.exportPivotFile('pdf') },
          { type: 'excel', action: () => this.exportPivotFile('xlsx') },
        ];
      }
      if (!this.rowData.length) return [];
      return [
        { type: 'pptx', action: () => this.exportFile('pptx') },
        { type: 'pdf', action: () => this.exportFile('pdf') },
        { type: 'excel', action: () => this.exportFile('xlsx') },
      ];
    },
    fetchActiveMode() {
      if (this.viewMode === 'pivot') {
        this.fetchPivotData();
      } else {
        this.fetchScorecardData();
      }
    },

    // ── PIVOT ──

    async fetchPivotData() {
      if (!this.pivotActiveMetricIds.length || !this.entity?.id) {
        this.pivotRawData = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await axios.post('api/impact_analytics/composable-metrics/run_multi/', {
          metric_ids: this.pivotActiveMetricIds,
          entity_type: this.pivotEntityType,
          entity_id: this.entity.id,
          observation_year: this.observationYear,
          group_by_contract: false,
          time_series: false,
        });
        this.pivotRawData = response.data?.results || [];
        // Backfill metric meta from response if we don't have it yet
        for (const row of this.pivotRawData) {
          if (row.metric_id && !this.pivotMetricMeta[row.metric_id]) {
            this.pivotMetricMeta[row.metric_id] = {
              name: row.metric_name,
              display_config: row.display_config || {},
            };
          }
        }
      } catch (error) {
        console.error('Error fetching pivot data:', error);
        this.pivotRawData = [];
      } finally {
        this.loading = false;
      }
    },

    addPivotMetric(id) {
      this.pivotRemovedMetricIds = this.pivotRemovedMetricIds.filter(m => m !== id);
      if (!this.metricIds.includes(id) && !this.pivotAddedMetricIds.includes(id)) {
        this.pivotAddedMetricIds.push(id);
      }
    },
    removePivotMetric(id) {
      this.pivotAddedMetricIds = this.pivotAddedMetricIds.filter(m => m !== id);
      if (!this.pivotRemovedMetricIds.includes(id)) {
        this.pivotRemovedMetricIds.push(id);
      }
    },

    doPivotSort(key) {
      if (this.pivotSortKey === key) {
        this.pivotSortAsc = !this.pivotSortAsc;
      } else {
        this.pivotSortKey = key;
        this.pivotSortAsc = true;
      }
    },

    onColumnDragStart(metricId) {
      this.dragColumnId = metricId;
    },
    onColumnDragOver(e, targetId) {
      e.preventDefault();
      if (this.dragColumnId == null || this.dragColumnId === targetId) return;
      const ids = this.pivotActiveMetrics.map(m => m.id);
      const fromIdx = ids.indexOf(this.dragColumnId);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      ids.splice(fromIdx, 1);
      ids.splice(toIdx, 0, this.dragColumnId);
      this.pivotMetricOrder = ids;
    },
    onColumnDragEnd() {
      this.dragColumnId = null;
    },

    onRowDragStart(metricId) {
      this.dragRowId = metricId;
    },
    onRowDragOver(e, targetId) {
      e.preventDefault();
      if (this.dragRowId == null || this.dragRowId === targetId) return;
      const ids = this.orderedRowData.map(r => r.metric_id);
      const fromIdx = ids.indexOf(this.dragRowId);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      ids.splice(fromIdx, 1);
      ids.splice(toIdx, 0, this.dragRowId);
      this.scorecardMetricOrder = ids;
    },
    onRowDragEnd() {
      this.dragRowId = null;
    },

    onGroupDragStart(groupId) {
      this.dragGroupId = groupId;
    },
    onGroupDragOver(e, targetId) {
      e.preventDefault();
      if (this.dragGroupId == null || this.dragGroupId === targetId) return;
      const ids = this.orderedScorecardGroups.map(g => g.id);
      const fromIdx = ids.indexOf(this.dragGroupId);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      ids.splice(fromIdx, 1);
      ids.splice(toIdx, 0, this.dragGroupId);
      this.scorecardColumnOrder = ids;
    },
    onGroupDragEnd() {
      this.dragGroupId = null;
    },

    pivotDisplayConfig(metric) {
      return metric?.display_config || {};
    },

    pivotTierColor(rate, metric, type) {
      if (rate == null) return type === 'text' ? 'text-gray-400' : 'bg-gray-200';
      const dc = this.pivotDisplayConfig(metric);
      const thresholds = dc.thresholds || [0.4, 0.7];
      const higherIsBetter = dc.higher_is_better !== false;
      const [lo, hi] = thresholds;
      const good = higherIsBetter ? rate >= hi : rate <= lo;
      const mid = higherIsBetter ? rate >= lo : rate <= hi;
      if (type === 'bar') return good ? 'bg-secondary-300' : mid ? 'bg-primary-200' : 'bg-red-light';
      return good ? 'text-secondary-800' : mid ? 'text-primary-700' : 'text-red-dark';
    },

    pivotBarColor(rate, metric) {
      return this.pivotTierColor(rate, metric, 'bar');
    },
    pivotTextColor(rate, metric) {
      return this.pivotTierColor(rate, metric, 'text');
    },
    pivotFormatRate(rate, metric) {
      if (rate == null) return '-';
      const dc = this.pivotDisplayConfig(metric);
      if (dc.format === 'decimal' || dc.format === 'number') return rate.toFixed(dc.decimal_places ?? 1);
      if (dc.format === 'count') return Math.round(rate).toLocaleString();
      return (rate * 100).toFixed(dc.decimal_places ?? 1) + '%';
    },

    showPivotTooltip(row, metric, event) {
      this.pivotTooltipMetric = metric;
      this.pivotTooltipRow = {
        rate: row['metric_' + metric.id],
        num: row['num_' + metric.id],
        denom: row['denom_' + metric.id],
        attribution: row['attr_' + metric.id],
      };
      this.pivotTooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },
    movePivotTooltip(event) {
      this.pivotTooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },

    async exportPivotFile(format) {
      try {
        const metrics = this.pivotActiveMetrics.map(m => ({
          id: m.id,
          name: m.name,
          display_config: m.display_config || {},
        }));
        const rows = this.pivotSorted.map(row => {
          const values = {};
          for (const m of this.pivotActiveMetrics) {
            values[m.id] = {
              rate: row['metric_' + m.id] ?? null,
              num: row['num_' + m.id] ?? null,
              denom: row['denom_' + m.id] ?? null,
              attr: row['attr_' + m.id] ?? null,
            };
          }
          return { entity_name: row.entity_name, values };
        });
        const response = await axios.post('api/impact_analytics/composable-metrics/export_pivot/', {
          entity_type: this.pivotEntityType,
          metrics,
          rows,
          entity_name: this.entity?.name || 'export',
          observation_year: this.observationYear,
          format,
        }, { responseType: 'blob' });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pivot_${this.entity?.name || 'export'}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`${format.toUpperCase()} pivot export failed:`, error);
      }
    },

    // ── SCORECARD ──

    async fetchScorecardData() {
      if (!this.activeMetricIds.length || !this.entity?.id) {
        this.rowData = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await axios.post('api/impact_analytics/composable-metrics/run_multi/', {
          metric_ids: this.activeMetricIds,
          entity_id: this.entity.id,
          observation_year: this.observationYear,
          group_by_contract: false,
          time_series: true,
        });
        this.rowData = this.buildRowData(response.data?.results || []);
      } catch (error) {
        console.error('Error fetching scorecard data:', error);
        this.rowData = [];
      } finally {
        this.loading = false;
      }
    },

    buildRowData(results) {
      const byMetric = {};
      for (const row of results) {
        const mid = row.metric_id;
        if (!byMetric[mid]) byMetric[mid] = { name: row.metric_name, rows: [] };
        byMetric[mid].rows.push(row);
      }

      const oy = this.observationYear || new Date().getFullYear();

      return this.activeMetricIds.map(mid => {
        const data = byMetric[mid];
        if (!data || !data.rows.length) return null;

        const rows = data.rows;
        const latest = rows[rows.length - 1];
        const prev = rows.length >= 5 ? rows[rows.length - 5] : null;

        const sparkline = rows
          .filter(r => {
            const yr = r.observation_date ? new Date(r.observation_date + 'T00:00:00').getFullYear() : null;
            return yr === oy;
          })
          .map(r => ({ d: r.observation_date, r: r.rate }));

        const currentYearRows = rows.filter(r => {
          const yr = r.observation_date ? new Date(r.observation_date + 'T00:00:00').getFullYear() : null;
          return yr === oy && r.rate != null;
        });
        const trendRows = currentYearRows.slice(-12);
        const trendSlope = this.regressSlope(trendRows);

        const config = latest.display_config || {};
        const fmt = config.format === 'percentage' ? '%'
          : config.format === 'currency' ? '$'
          : config.format === 'per_1k' ? '/1k'
          : config.format === 'number' ? '#'
          : config.format === 'decimal' ? 'dec'
          : config.format === 'count' ? '#'
          : '%';
        const higherIsBetter = config.higher_is_better !== false;

        return {
          metric_id: mid,
          metric_name: data.name,
          metric_formatting: fmt,
          metric_is_reverse: !higherIsBetter,
          numerator: latest.numerator,
          denominator: latest.denominator,
          attribution: latest.attribution,
          observation_date: latest.observation_date,
          most_recent_ov_claim_date: latest.most_recent_ov_claim_date,
          prev_numerator: prev ? prev.numerator : null,
          prev_denominator: prev ? prev.denominator : null,
          trend_slope: trendSlope,
          sparkline,
          target_value: config.monthly_targets?.[11] ?? config.thresholds?.[1] ?? null,
          metadata: latest.metadata || null,
        };
      }).filter(Boolean);
    },

    regressSlope(rows) {
      if (rows.length < 3) return null;
      const epoch = new Date('2020-01-01T00:00:00').getTime();
      let n = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
      for (const r of rows) {
        if (r.rate == null) continue;
        const x = (new Date(r.observation_date + 'T00:00:00').getTime() - epoch) / 86400000;
        const y = r.rate;
        n++; sx += x; sy += y; sxx += x * x; sxy += x * y;
      }
      if (n < 3) return null;
      const denom = n * sxx - sx * sx;
      if (denom === 0) return null;
      return (n * sxy - sx * sy) / denom;
    },

    sparkMinMax(row) {
      const rates = row.sparkline.map(p => p.r).filter(r => r != null);
      if (!rates.length) return { min: 0, max: 1 };
      const min = Math.min(...rates);
      const max = Math.max(...rates);
      return { min: min === max ? min - 0.01 : min, max: max === min ? max + 0.01 : max };
    },
    sparkX(row, i) {
      const n = row.sparkline.length;
      const p = this.sparkPadding;
      return p + (i / (n - 1)) * (this.sparkWidth - 2 * p);
    },
    sparkY(row, rate) {
      if (rate == null) return this.sparkHeight / 2;
      const { min, max } = this.sparkMinMax(row);
      const p = this.sparkPadding;
      return p + (1 - (rate - min) / (max - min)) * (this.sparkHeight - 2 * p);
    },
    sparkPath(row) {
      return row.sparkline
        .map((p, i) => p.r != null ? `${i === 0 ? 'M' : 'L'}${this.sparkX(row, i)},${this.sparkY(row, p.r)}` : null)
        .filter(Boolean).join(' ');
    },
    sparkEndColor(row) {
      const s = row.sparkline;
      if (s.length < 2) return '#94a3b8';
      const last = s[s.length - 1].r;
      const prev = s[s.length - 2].r;
      if (last == null || prev == null) return '#94a3b8';
      const up = last >= prev;
      const good = row.metric_is_reverse ? !up : up;
      return good ? '#16a34a' : '#dc2626';
    },

    metricLink(row) {
      const base = '/insight';
      return `${base}/metric/${row.metric_id}/${this.entity.id}`;
    },
    formatRate(row) {
      const denom = row.denominator || 0;
      if (denom === 0) return '—';
      if (row.metric_formatting === '#') return this.formatRateValue(row.numerator, row.metric_formatting);
      return this.formatRateValue(row.numerator / denom, row.metric_formatting);
    },
    formatPrevRate(row) {
      if (row.prev_numerator == null || row.prev_denominator == null) return '—';
      const denom = row.prev_denominator || 0;
      if (denom === 0) return '—';
      return this.formatRateValue(row.prev_numerator / denom, row.metric_formatting);
    },
    formatRateValue(rate, formatting) {
      if (formatting === '%') return (rate * 100).toFixed(1) + '%';
      if (formatting === '$') return '$' + Math.round(rate).toLocaleString();
      if (formatting === '/1k') return Math.round(rate).toLocaleString() + '/1k';
      if (formatting === '#') return Math.round(rate).toLocaleString();
      if (formatting === 'dec') return rate.toFixed(2);
      return rate.toFixed(2);
    },
    formatTrend(row) {
      if (row.trend_slope == null) return '';
      const slopePerMonth = row.trend_slope * 30;
      const sign = slopePerMonth > 0 ? '+' : '';
      if (row.metric_formatting === '%') return `${sign}${(slopePerMonth * 100).toFixed(2)}%`;
      return `${sign}${slopePerMonth.toFixed(2)}`;
    },
    trendClass(row) {
      if (row.trend_slope == null) return 'text-gray-400';
      const higher = !row.metric_is_reverse;
      const positive = row.trend_slope > 0;
      return (higher ? positive : !positive) ? 'text-green-600' : 'text-red-600';
    },
    rateClass(row) {
      if (!row.denominator) return 'text-gray-400';
      return 'text-gray-900';
    },
    remainingOpp(row) {
      const num = row.numerator || 0;
      const denom = row.denominator || 0;
      return num < denom ? denom - num : 0;
    },
    numNeededPerDay(row) {
      if (row.target_value == null || !row.denominator) return null;
      const needed = Math.max(row.target_value * row.denominator - (row.numerator || 0), 0);
      const today = new Date();
      const yearEnd = new Date(this.observationYear || today.getFullYear(), 11, 31);
      const daysRemaining = Math.max(Math.ceil((yearEnd - today) / 86400000), 1);
      return needed / daysRemaining;
    },
    formatTarget(row) {
      const npd = this.numNeededPerDay(row);
      if (npd == null) return '—';
      return Math.ceil(npd * 30).toLocaleString();
    },
    formatNumber(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString();
    },
    isYTD(row) {
      const s = row.metadata?.denom_start_date;
      if (!s) return true;
      const d = new Date(s + 'T00:00:00');
      return d.getMonth() === 0 && d.getDate() === 1;
    },
    formatDate(val) {
      if (!val) return '—';
      return new Date(val + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },


    async exportFile(format) {
      try {
        const rows = this.orderedRowData.map(row => ({
          metric_name: row.metric_name,
          metric_formatting: row.metric_formatting,
          metric_is_reverse: row.metric_is_reverse,
          denom_start_date: row.metadata?.denom_start_date || null,
          denom_end_date: row.metadata?.denom_end_date || null,
          numerator: row.numerator,
          denominator: row.denominator,
          attribution: row.attribution,
          last_month_numerator: row.prev_numerator,
          last_month_denominator: row.prev_denominator,
          numerator_last_month: row.prev_numerator,
          trend_slope: row.trend_slope,
          num_needed_per_day: this.numNeededPerDay(row),
          observation_date: row.observation_date,
          most_recent_ov_claim_date: row.most_recent_ov_claim_date,
          is_ytd: this.isYTD(row),
        }));
        const response = await axios.post('api/impact_analytics/composable-metrics/export_scorecard/', {
          rows,
          entity_name: this.entity?.name || 'export',
          observation_year: this.observationYear,
          format,
          column_order: this.orderedScorecardGroups.map(g => g.id),
        }, { responseType: 'blob' });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `scorecard_${this.entity?.name || 'export'}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`${format.toUpperCase()} export failed:`, error);
      }
    },


  },
};
</script>
